import { useEffect, useMemo, useState } from 'react';
import reviewsData from '../content/fallback-reviews.json';

type Review = {
  author_name?: string;
  rating?: number;
  relative_time_description?: string;
  text?: string;
};

const REVIEWS: Review[] = (reviewsData as any).items || [];
const GOOGLE_LISTING_URL =
  'https://www.google.com/maps/search/?api=1&query=' +
  encodeURIComponent('CAVASSANI BRAZILIAN JIUJITSU, 855 Marina Bay Pkwy Ste 110, Richmond, CA 94804');
const GOOGLE_WRITE_REVIEW_URL =
  'https://search.google.com/local/writereview?placeid=ChIJW-bLITeDhYARtP-23lAxeEA';

function Stars({ rating, label }: { rating: number; label?: string }) {
  return (
    <div className="stars" aria-label={label ?? `${rating} stars`}>
      {Array.from({ length: 5 }).map((_, j) => (
        <span key={j} className={j < Math.round(rating) ? '' : 'stars-dim'}>
          ★
        </span>
      ))}
    </div>
  );
}

function ReviewModal({ review, onClose }: { review: Review; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      className="lightbox-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`Review by ${review.author_name || 'Student'}`}
      onClick={onClose}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="lightbox-panel modal-panel review-modal-panel"
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="lightbox-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className="review-modal-head">
          <div className="avatar review-modal-avatar" aria-hidden>
            <span>{(review.author_name || 'S').charAt(0)}</span>
          </div>
          <div className="review-modal-meta">
            <strong>{review.author_name || 'Student'}</strong>
            <Stars rating={review.rating || 5} />
            <small className="fine">{review.relative_time_description}</small>
          </div>
        </div>
        <p className="review-modal-text">{review.text}</p>
      </div>
    </div>
  );
}

export default function ManualReviews() {
  const [start, setStart] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [modal, setModal] = useState<Review | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 760px)');
    const update = () => setVisibleCount(mq.matches ? 1 : 3);
    update();
    try {
      (mq as any).addEventListener('change', update);
    } catch {
      (mq as any).addListener?.(update);
    }
    return () => {
      try {
        (mq as any).removeEventListener('change', update);
      } catch {
        (mq as any).removeListener?.(update);
      }
    };
  }, []);

  const display = useMemo<Review[]>(() => REVIEWS.filter((review) => review.text), []);

  if (!display.length) return <p className="fine">No reviews published yet.</p>;

  const visible = Array.from({
    length: Math.min(visibleCount, display.length || visibleCount),
  }).map((_, idx) => display[(start + idx) % (display.length || 1)]);

  return (
    <div>
      <div className="reviews-slider">
        <button
          className="slider-nav prev"
          aria-label="Previous reviews"
          onClick={() => setStart((start - 1 + display.length) % display.length)}
        ></button>
        <div className="slider-window">
          {visible.map((review, index) => (
            <div
              className="review-card"
              key={`${review.author_name || 'student'}-${index}-${start}`}
              onClick={() => setModal(review)}
              role="button"
              tabIndex={0}
              aria-label={`Read full review by ${review.author_name || 'Student'}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setModal(review);
              }}
            >
              <div className="review-head">
                <div className="avatar" aria-hidden>
                  <span>{(review.author_name || 'S').charAt(0)}</span>
                </div>
                <div>
                  <strong>{review.author_name || 'Student'}</strong>
                  <Stars rating={review.rating || 5} label={`${review.rating || 5} stars`} />
                  <small className="fine">{review.relative_time_description}</small>
                </div>
              </div>
              <p className="review-text review-text-clamp">{review.text}</p>
              <span className="review-read-more">Read more</span>
            </div>
          ))}
        </div>
        <button
          className="slider-nav next"
          aria-label="Next reviews"
          onClick={() => setStart((start + 1) % display.length)}
        ></button>
      </div>
      <div className="reviews-cta">
        <a
          href={GOOGLE_LISTING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline"
        >
          More on Google
        </a>
        <a
          href={GOOGLE_WRITE_REVIEW_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-accent"
        >
          Leave a Review
        </a>
      </div>

      {modal && <ReviewModal review={modal} onClose={() => setModal(null)} />}
    </div>
  );
}
